import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import * as Icons from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { INDUSTRIES } from "@/data/site";

function IconBlock({ name, className = "" }) {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} strokeWidth={1.5} />;
}

const VALUES = [
  { t: "Build", d: "We engineer software platforms and digital business systems built to scale, automate, and outperform." },
  { t: "Acquire", d: "We strategically acquire and curate premium domain portfolios as long-term digital real estate." },
  { t: "Consult", d: "We advise founders, investors, and operators on technology, growth, and digital asset strategy." },
  { t: "Scale", d: "We deploy lead-generation, automation, and optimization systems that compound results over time." },
];

export default function About() {
  return (
    <>
      <section className="pt-32 pb-16 md:pt-40 relative" data-testid="about-page">
        <div className="absolute inset-0 grid-bg radial-mask opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="eyebrow mb-4">About</div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900">
                A technology company founded to build, acquire, and scale.
              </h1>
              <p className="mt-6 text-base md:text-lg text-slate-700 max-w-2xl leading-relaxed">
                S&amp;B Technologies was founded by{" "}
                <span className="text-slate-900 font-medium">Shawn Williams</span> and{" "}
                <span className="text-slate-900 font-medium">Brittney Williams</span> as a
                technology company focused on software development, digital transformation,
                consulting, domain strategy, and online business growth.
              </p>
              <p className="mt-4 text-base text-slate-600 max-w-2xl leading-relaxed">
                We help businesses create systems that generate leads, manage operations,
                automate workflows, launch websites, build SaaS products, and turn digital
                ideas into scalable platforms.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="tracing-card p-8">
                <div className="eyebrow mb-4">// Founders</div>
                <div className="space-y-6">
                  {[
                    { n: "Shawn Williams", r: "Co-Founder" },
                    { n: "Brittney Williams", r: "Co-Founder" },
                  ].map((f) => (
                    <div key={f.n} className="flex items-center gap-4">
                      <div className="h-14 w-14 border border-[#0066FF]/40 bg-white flex items-center justify-center">
                        <span className="font-display text-lg font-semibold text-[#0066FF]">
                          {f.n.split(" ").map((p) => p[0]).join("")}
                        </span>
                      </div>
                      <div>
                        <div className="font-display text-base font-semibold text-slate-900">{f.n}</div>
                        <div className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500 mt-1">
                          {f.r}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Operating Principles"
            title="Build. Acquire. Consult. Scale."
            description="Four pillars define how we work — and how we choose what to work on."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {VALUES.map((v, i) => (
              <div key={v.t} className="tracing-card p-8" data-testid={`about-value-${i}`}>
                <div className="font-mono text-xs text-slate-400">/ 0{i + 1}</div>
                <h3 className="mt-3 font-display text-2xl font-semibold text-slate-900">{v.t}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Industries"
            title="Verticals we serve."
          />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {INDUSTRIES.map((ind, i) => (
              <div
                key={ind.name}
                className="tracing-card p-6 flex flex-col items-center text-center"
                data-testid={`about-industry-${i}`}
              >
                <div className="flex h-12 w-12 items-center justify-center border border-[#0066FF]/30 text-[#0066FF] mb-4">
                  <IconBlock name={ind.icon} className="h-5 w-5" />
                </div>
                <div className="font-display text-sm font-semibold text-slate-900">{ind.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <SectionHeader
            align="center"
            eyebrow="Get In Touch"
            title="Let's build something powerful."
          />
          <Link
            to="/contact"
            className="mt-8 btn-primary inline-flex"
            data-testid="about-cta-btn"
          >
            Contact S&amp;B Technologies <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}
